import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  Visibility,
  Delete,
} from '@mui/icons-material';
import apiService from '../services/apiService';

const RidesPage = () => {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);

  const fetchRides = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const response = await apiService.getRides(params);

      if (response.success) {
        setRides(response.data);
      } else {
        setError(response.message || 'Failed to load rides');
        setRides([]);
      }
    } catch (err) {
      console.error('Rides error:', err);
      setError('Failed to load rides');
      setRides([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    fetchRides();
  }, [fetchRides]);

  const handleViewRide = async (ride) => {
    setSelectedRide(ride);
    setOpenDialog(true);
  };

  const handleDeleteRide = async (rideId) => {
    if (!window.confirm('Are you sure you want to delete this ride? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.deleteRide(rideId);
      if (response.success) {
        alert('Ride deleted successfully');
        fetchRides();
      } else {
        alert(response.message || 'Failed to delete ride');
      }
    } catch (err) {
      console.error('Delete ride error:', err);
      alert('Failed to delete ride');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      searching_driver: { label: 'Searching Driver', color: 'warning' },
      driver_assigned: { label: 'Driver Assigned', color: 'info' },
      driver_arrived: { label: 'Driver Arrived', color: 'info' },
      in_progress: { label: 'In Progress', color: 'primary' },
      completed: { label: 'Completed', color: 'success' },
      cancelled: { label: 'Cancelled', color: 'error' },
    };

    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const columns = [
    {
      field: 'crn',
      headerName: 'CRN',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
          {params.row.crn || params.row.id?.substring(0, 12) + '...'}
        </Typography>
      ),
    },
    {
      field: 'passengerName',
      headerName: 'Passenger',
      width: 130,
      renderCell: (params) => params.row.passengerName || '-',
    },
    {
      field: 'driverName',
      headerName: 'Driver',
      width: 130,
      renderCell: (params) => params.row.driverName || '-',
    },
    {
      field: 'vehicleType',
      headerName: 'Vehicle',
      width: 120,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {params.row.vehicleType || '-'}
          </Typography>
          {params.row.vehicleNumber && (
            <Typography variant="caption" color="text.secondary">
              {params.row.vehicleNumber}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'pickupLocation',
      headerName: 'Pickup',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.row.pickupLocation}>
          {params.row.pickupLocation || '-'}
        </Typography>
      ),
    },
    {
      field: 'dropLocation',
      headerName: 'Drop',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.row.dropLocation}>
          {params.row.dropLocation || '-'}
        </Typography>
      ),
    },
    {
      field: 'fare',
      headerName: 'Fare',
      width: 100,
      renderCell: (params) => {
        const fare = params.row.fare || params.row.finalFare || params.row.estimatedFare;
        return fare > 0 ? `₹${parseFloat(fare).toFixed(0)}` : '-';
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => getStatusChip(params.row.status),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 160,
      renderCell: (params) => formatDate(params.row.createdAt),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleViewRide(params.row)}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteRide(params.row.id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Server-side search is now used - filteredRides directly uses rides
  const filteredRides = rides;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Rides
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage all rides
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error} - Showing demo data
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              placeholder="Search by CRN, passenger, or driver..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchRides(); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="searching_driver">Searching Driver</MenuItem>
                <MenuItem value="driver_assigned">Driver Assigned</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <DataGrid
            rows={filteredRides}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* View Ride Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ride Details</DialogTitle>
        <DialogContent>
          {selectedRide && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontFamily: 'monospace' }}>
                    CRN: {selectedRide.crn || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Ride ID: {selectedRide.id}
                  </Typography>
                  <Box sx={{ mb: 2 }}>{getStatusChip(selectedRide.status)}</Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Passenger</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedRide.passengerName || '-'}</Typography>
                  {selectedRide.passengerPhone && (
                    <Typography variant="body2" color="text.secondary">{selectedRide.passengerPhone}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Driver</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedRide.driverName || 'Not assigned'}
                  </Typography>
                  {selectedRide.driverPhone && (
                    <Typography variant="body2" color="text.secondary">{selectedRide.driverPhone}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Vehicle Type</Typography>
                  <Typography variant="body1">{selectedRide.vehicleType || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Vehicle Number</Typography>
                  <Typography variant="body1">{selectedRide.vehicleNumber || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Pickup Location</Typography>
                  <Typography variant="body1">{selectedRide.pickupLocation || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Drop Location</Typography>
                  <Typography variant="body1">{selectedRide.dropLocation || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Distance</Typography>
                  <Typography variant="body1">
                    {selectedRide.distance > 0 ? `${parseFloat(selectedRide.distance).toFixed(1)} km` : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Fare</Typography>
                  <Typography variant="body1" fontWeight={600} color="primary.main">
                    {(selectedRide.fare || selectedRide.finalFare || selectedRide.estimatedFare) > 0
                      ? `₹${parseFloat(selectedRide.fare || selectedRide.finalFare || selectedRide.estimatedFare).toFixed(0)}`
                      : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {selectedRide.paymentMethod || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {selectedRide.paymentStatus || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Created At</Typography>
                  <Typography variant="body1">{formatDate(selectedRide.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRide.status === 'completed' ? 'Completed At' :
                     selectedRide.status === 'cancelled' ? 'Cancelled At' : 'Accepted At'}
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedRide.completedAt || selectedRide.cancelledAt || selectedRide.acceptedAt)}
                  </Typography>
                </Grid>
                {selectedRide.cancellationReason && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Cancellation Reason</Typography>
                    <Typography variant="body1" color="error.main">{selectedRide.cancellationReason}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RidesPage;
